/**
 * Черновик крупного действия: что показать до подтверждения.
 *
 * Разбор живёт отдельно от компонента, потому что «что именно собирается
 * сделать модель» читается из аргументов вызова, а их форма — договор с
 * реестром инструментов бэкенда, а не с вёрсткой.
 */

/** Одна задача пачки в том виде, в каком её показывает таблица (макет 8-3). */
export interface DraftIssue {
  title: string
  /** Значения реестра: task, bug, story, epic, subtask. */
  type: string
  /** Значения реестра: lowest…highest. */
  priority: string
}

/** Поле задачи, которое переписывают (макет 8-4). */
export interface DraftField {
  name: 'title' | 'description_md'
  after: string
}

/**
 * Что нарисовать вместо аргументов вызова.
 *
 * Разбор идёт по имени инструмента, а не по форме аргументов: имена — тот же
 * договор, что и схема, и неизвестный инструмент обязан доехать до экрана
 * сырьём, а не пропасть. Пропавший черновик оставил бы разговор в тупике:
 * подтверждать нечего, а ход стоит и ждёт человека.
 */
export type DraftView =
  | { kind: 'issues'; issues: DraftIssue[]; epicKey: string; projectKey: string }
  | { kind: 'fields'; issueKey: string; fields: DraftField[] }
  | { kind: 'raw' }

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const asText = (value: unknown): string => (typeof value === 'string' ? value : '')

const asIssue = (value: unknown): DraftIssue => {
  const item = asRecord(value)

  return {
    title: asText(item?.title),
    type: asText(item?.type) || 'task',
    priority: asText(item?.priority) || 'medium',
  }
}

/** Поля, переписывание которых бэкенд считает крупным действием. */
const LARGE_FIELDS: DraftField['name'][] = ['title', 'description_md']

export const describeDraft = (name: string, args: unknown): DraftView => {
  const input = asRecord(args) ?? {}

  if (name === 'create_issues_batch') {
    const issues = Array.isArray(input.issues) ? input.issues.map(asIssue) : []

    return {
      kind: 'issues',
      issues,
      epicKey: asText(input.epic_key),
      projectKey: asText(input.project),
    }
  }

  if (name === 'create_issue') {
    return {
      kind: 'issues',
      issues: [asIssue(input)],
      epicKey: asText(input.parent_key),
      projectKey: asText(input.project),
    }
  }

  if (name === 'update_issue') {
    const fields = LARGE_FIELDS.filter((field) => typeof input[field] === 'string').map((field) => ({
      name: field,
      after: asText(input[field]),
    }))

    // Мелкие поля крупным действием не бывают: приоритет и исполнителя бэкенд
    // применяет сразу. Если в вызове их нет — показывать нечего, и разбор
    // честнее отдать сырьём, чем нарисовать пустой diff.
    if (fields.length) return { kind: 'fields', issueKey: asText(input.issue), fields }
  }

  return { kind: 'raw' }
}

/** Строка сравнения: убранная или добавленная. Неизменённые не показываются. */
export interface DiffLine {
  added: boolean
  text: string
}

/**
 * Сравнение по строкам, сгруппированное по местам правки.
 *
 * Показываются только изменённые строки: описание задачи бывает на сотню строк,
 * и целиком оно вытеснит с экрана и сам разговор, и кнопки под черновиком.
 * Пропущенное между группами обозначается разрывом — так видно, что правка не
 * одна.
 *
 * Наибольшая общая подпоследовательность считается таблицей: описания короткие,
 * и сложность здесь дешевле неверного сравнения, при котором сдвиг на одну
 * строку красит в «изменилось» весь остаток текста.
 */
export const diffHunks = (before: string, after: string): DiffLine[][] => {
  const source = before ? before.split('\n') : []
  const target = after ? after.split('\n') : []

  const width = target.length + 1
  const common = new Int32Array((source.length + 1) * width)
  const at = (i: number, j: number): number => common[i * width + j] ?? 0

  for (let i = source.length - 1; i >= 0; i--) {
    for (let j = target.length - 1; j >= 0; j--) {
      common[i * width + j] =
        source[i] === target[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1))
    }
  }

  const hunks: DiffLine[][] = []
  let hunk: DiffLine[] = []

  // Разрыв закрывает группу: следующая правка начнёт свою.
  const cut = () => {
    if (hunk.length) hunks.push(hunk)

    hunk = []
  }

  let i = 0
  let j = 0

  while (i < source.length && j < target.length) {
    if (source[i] === target[j]) {
      cut()
      i++
      j++

      continue
    }

    if (at(i + 1, j) >= at(i, j + 1)) {
      hunk.push({ added: false, text: source[i] ?? '' })
      i++
    } else {
      hunk.push({ added: true, text: target[j] ?? '' })
      j++
    }
  }

  for (; i < source.length; i++) hunk.push({ added: false, text: source[i] ?? '' })
  for (; j < target.length; j++) hunk.push({ added: true, text: target[j] ?? '' })

  cut()

  return hunks
}
